import { isNotComing } from "../event-registrations/notComing";
import type { EventGroup, Registration } from "../event-registrations/shared";

/**
 * How many people each email reaches, counted the way the backend picks
 * its recipients: one email per address (the same address on two
 * bookings gets one email), and only bookings with an address. A booking
 * marked not coming (seva) gets none of the emails for people who are
 * coming. These counts are for the page; the backend works out the real
 * list itself when it sends.
 */

/** The backend's check for a usable address on the thank-you emails. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function addressOf(registration: Registration): string {
  return registration.email.trim().toLowerCase();
}

function distinctAddresses(
  registrations: readonly Registration[],
  include: (registration: Registration) => boolean,
): Set<string> {
  const addresses = new Set<string>();
  for (const registration of registrations) {
    const address = addressOf(registration);
    if (address && include(registration)) {
      addresses.add(address);
    }
  }
  return addresses;
}

/** Everyone who booked and is coming, walk-ins included. */
export function reminderAudience(group: EventGroup): number {
  return distinctAddresses(group.registrations, (r) => !isNotComing(r)).size;
}

/** Everyone who booked and is coming, not walk-ins: they're already
 *  through the door. */
export function entryPassAudience(group: EventGroup): number {
  return distinctAddresses(
    group.registrations,
    (r) => !r.isWalkIn && !isNotComing(r),
  ).size;
}

/** Coming bookings with a Kund allocated and an address (the "Your Kund"
 *  email), and those still waiting for one. */
export function kundAudience(group: EventGroup) {
  const coming = group.registrations.filter(
    (r) => !r.isWalkIn && !isNotComing(r) && addressOf(r),
  );
  const withKund = new Set(
    coming.filter((r) => r.kundPlace).map((r) => addressOf(r)),
  ).size;
  return { withKund, withoutKund: coming.filter((r) => !r.kundPlace).length };
}

/** People who haven't been checked in yet and are still expected. */
export function notArrivedAudience(group: EventGroup): number {
  return distinctAddresses(
    group.registrations,
    (r) => !r.checkedInAt && !isNotComing(r),
  ).size;
}

/** People who came ("attended") or who booked and didn't ("absent"). A
 *  booking marked not coming is in neither. */
export function followUpAudience(
  group: EventGroup,
  audience: "attended" | "absent",
): number {
  const wantCame = audience === "attended";
  return distinctAddresses(
    group.registrations,
    (r) =>
      Boolean(r.checkedInAt) === wantCame &&
      !isNotComing(r) &&
      EMAIL_PATTERN.test(addressOf(r)),
  ).size;
}

/** Everyone who has booked for any event, less anyone already booked for
 *  this one (they get the reminder instead). */
export function pastGuestAudience(
  events: readonly EventGroup[],
  group: EventGroup,
): number {
  const bookedHere = distinctAddresses(group.registrations, () => true);
  const guests = new Set<string>();
  for (const event of events) {
    for (const address of distinctAddresses(event.registrations, () => true)) {
      if (!bookedHere.has(address)) {
        guests.add(address);
      }
    }
  }
  return guests.size;
}
